import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  ReactFlowProvider,
  useReactFlow,
  ControlButton,
  getBezierPath,
  EdgeProps
} from 'reactflow';
import { debounce } from 'lodash';
import 'reactflow/dist/style.css';
import { FiFilter, FiSearch, FiMaximize, FiZoomIn, FiZoomOut, FiX, FiRefreshCw } from 'react-icons/fi';
import { useTheme } from '../../../contexts/ThemeContext';
import { NodeStatus, ProjectManifest } from '../../../types/artifactory';
import api from '../../../services/api';

// Import custom components and utilities
import FileNode from './nodes/FileNode';
import DirectoryNode from './nodes/DirectoryNode';
import { HydrationRowCard } from './HydrationRowCard';
import { 
  getLayoutedElements, 
  getForceLayoutedElements, 
  getRadialLayoutedElements 
} from './layouts/GraphLayouts';
import { 
  groupNodesByDAG, 
  applyGroupStyling, 
  getStatusColor, 
  getEdgeColor,
  findNodeDependencies,
  findRootNodes
} from './utils/GraphUtils';

// Custom Controls component that supports dark mode and has a glow effect
const CustomControls = ({ darkMode, onZoomIn, onZoomOut, onFitView, showAllNodes, onToggleAllNodes, onRefresh }) => {
  return (
    <div className={`react-flow__controls ${darkMode ? 'dark-mode-controls' : ''}`}>
      <div className={`custom-controls-container ${darkMode ? 'dark' : 'light'}`}>
        <button 
          className="custom-control-button" 
          onClick={onZoomIn}
          title="Zoom in"
        >
          <FiZoomIn />
        </button>
        <button 
          className="custom-control-button" 
          onClick={onZoomOut}
          title="Zoom out"
        >
          <FiZoomOut />
        </button>
        <button 
          className="custom-control-button" 
          onClick={onFitView}
          title="Fit view"
        >
          <FiX />
        </button>
        <button 
          className="custom-control-button" 
          onClick={onRefresh}
          title="Refresh graph"
        >
          <FiRefreshCw />
        </button>
        <button 
          className={`custom-control-button ${showAllNodes ? 'active-control' : ''}`} 
          onClick={onToggleAllNodes}
          title={showAllNodes ? "Show only hydration plan nodes" : "Show all project nodes"}
        >
          <FiFilter />
        </button>
      </div>
      <style jsx>{`
        .react-flow__controls {
          position: absolute;
          left: 10px;
          top: 10px;
          z-index: 5;
          font-size: 12px;
        }
        
        .custom-controls-container {
          display: flex;
          flex-direction: column;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
          border-radius: 5px;
          overflow: hidden;
          width: 32px;
        }
        
        .custom-controls-container.dark {
          background-color: #1f2937;
          border: 1px solid #374151;
          box-shadow: 0 0 10px rgba(59, 130, 246, 0.2);
        }
        
        .custom-controls-container.light {
          background-color: white;
          border: 1px solid #e5e7eb;
        }
        
        .custom-control-button {
          background: none;
          border: none;
          padding: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          height: 32px;
          width: 32px;
        }
        
        .dark .custom-control-button {
          color: #9ca3af;
        }
        
        .light .custom-control-button {
          color: #4b5563;
        }
        
        .dark .custom-control-button:hover {
          background-color: #374151;
          color: #60a5fa;
        }
        
        .light .custom-control-button:hover {
          background-color: #f3f4f6;
          color: #3b82f6;
        }
        
        .custom-control-button.active-control {
          background-color: #3b82f6;
          color: white;
        }
        
        .dark .custom-control-button.active-control {
          background-color: #2563eb;
          color: white;
        }
        
        .dark-mode-controls {
          filter: drop-shadow(0 0 3px rgba(59, 130, 246, 0.3));
        }
      `}</style>
    </div>
  );
};

// Use a constant for layout type - only Bottom to Top
const LAYOUT_TYPE = 'dagre-bt';

// Custom Edge component for better visualization
const CustomEdge = ({ id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, data }: EdgeProps) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Determine if this edge should be animated
  const isAnimated = data?.status === 'hydrating';
  
  return (
    <>
      <path
        id={id}
        className={`react-flow__edge-path ${isAnimated ? 'animated-edge' : ''}`}
        d={edgePath}
        style={style}
        markerEnd={markerEnd}
      />
      {/* Add a glow effect path underneath for important edges */}
      {style?.filter && (
        <path
          d={edgePath}
          style={{
            ...style,
            strokeWidth: (style.strokeWidth as number) + 2,
            opacity: 0.3,
            zIndex: -1
          }}
        />
      )}
    </>
  );
};

// Custom node types and edge types
const nodeTypes = {
  file: FileNode,
  directory: DirectoryNode
};

const edgeTypes = {
  custom: CustomEdge
};

interface DependencyGraphProps {
  focusNodeId: string | null;
  manifest: ProjectManifest | null;
  nodeStatuses: Record<string, NodeStatus>;
  completedNodes: Set<string>;
  maxHeight?: string;
  hydrationPlan?: any;
  currentlyHydrating?: string | null;
  failedNodes?: Set<string>;
  onFocusNode?: (focusCallback: (nodeId: string) => void) => void; // Add this new prop
}

// Create an inner component that uses the React Flow hooks
const DependencyGraphInner: React.FC<DependencyGraphProps> = ({ 
  focusNodeId, 
  manifest, 
  nodeStatuses, 
  completedNodes,
  maxHeight = '250px',
  hydrationPlan,
  currentlyHydrating = null,
  failedNodes = new Set(),
  onFocusNode // Add this new prop
}) => {
  // Always show all files and directories
  const showAllNodes = true;
  const showAllDirectories = true;
  const { darkMode } = useTheme();
  // No need for layout type state anymore
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Add refs to track layout state
  const initialLayoutApplied = useRef(false);
  const prevNodeCount = useRef(0);
  
  // Transform dependency tree into React Flow nodes and edges
  const { initialNodes, initialEdges } = useMemo(() => {
    console.log("=== DEPENDENCY GRAPH DATA ===");
    console.log("Focus Node ID:", focusNodeId);
    console.log("Hydration Plan:", hydrationPlan);
    console.log("Total manifest nodes:", manifest?.nodes ? Object.keys(manifest.nodes).length : 0);
    
    // If no hydration plan or manifest, return empty arrays
    if (!hydrationPlan?.ordered_plan && (!manifest?.nodes || Object.keys(manifest.nodes).length === 0)) {
      return { initialNodes: [], initialEdges: [] };
    }
    
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    
    // Determine which nodes to process
    let nodesToProcess: any[] = [];
    
    // When in focus node view, only show nodes from the hydration plan's ordered_plan
    if (focusNodeId && hydrationPlan?.ordered_plan) {
      // Use the same logic as HydrationPlanTable to determine which nodes to show
      const focusNodeIsDirectory = manifest?.nodes[focusNodeId]?.type === 'directory';
      
      if (focusNodeIsDirectory) {
        // For directories, show all file nodes that are children of this directory
        nodesToProcess = Object.values(manifest.nodes)
          .filter(node => 
            node.type === 'file' && 
            node.path.startsWith(manifest.nodes[focusNodeId].path + '/')
          );
      } else {
        // For files, show only the nodes in the hydration plan
        nodesToProcess = hydrationPlan.ordered_plan;
      }
    } else if (!focusNodeId && hydrationPlan?.ordered_plan) {
      // When in root node view (no focusNodeId), show all nodes from the hydration plan
      nodesToProcess = hydrationPlan.ordered_plan;
    } else if (showAllNodes && manifest?.nodes) {
      // When showing all nodes, filter based on the directory toggle
      nodesToProcess = Object.values(manifest.nodes).filter(node => {
        // Include all files, but only include directories if showAllDirectories is true
        return node.type !== 'directory' || showAllDirectories;
      });
    } else if (hydrationPlan?.ordered_plan) {
      // When showing only hydration plan nodes, still respect the directory toggle
      nodesToProcess = hydrationPlan.ordered_plan.filter(node => {
        return node.type !== 'directory' || showAllDirectories;
      });
    }
    
    console.log(`Processing ${nodesToProcess.length} nodes with showAllNodes=${showAllNodes} and showAllDirectories=${showAllDirectories}`);
    
    console.log(`Processing ${nodesToProcess.length} nodes for graph display`);
    
    // Process nodes
    nodesToProcess.forEach((node: any, index: number) => {
      const fileName = node.path.split('/').pop() || node.path;
      console.log(`Adding node to graph: ${node.id} (${node.path})`);
      console.log(`Node ${node.id} dependencies:`, node.dependencies);
      if (node.dependencies && node.dependencies.length > 0) {
        console.log("First dependency format:", typeof node.dependencies[0], node.dependencies[0]);
      }
      
      // Determine the node status with proper priority:
      // 1. If it's in completedNodes, mark as "fresh"
      // 2. If it's currently hydrating, mark as "hydrating"
      // 3. Use the status from nodeStatuses if available
      // 4. Fall back to the node's own status
      let status = node.status;
      if (completedNodes.has(node.id)) {
        status = 'fresh';
      } else if (currentlyHydrating === node.id) {
        status = 'hydrating';
      } else if (nodeStatuses[node.id]) {
        status = nodeStatuses[node.id];
      }
      
      const isCompleted = completedNodes.has(node.id);
      const isFocusNode = node.id === focusNodeId;
      
      // Determine if this is a directory or file node
      const isDirectory = node.type === 'directory';
      
      // Check if this node is in the hydration plan
      const isInHydrationPlan = !showAllNodes || 
        (hydrationPlan?.ordered_plan && hydrationPlan.ordered_plan.some(n => n.id === node.id));
      
      nodes.push({
        id: node.id,
        // Use the appropriate node type based on node.type
        type: isDirectory ? 'directory' : 'file',
        position: { x: 0, y: index * 100 }, // Initial position, will be adjusted by layout
        data: {
          label: fileName,
          path: node.path,
          status,
          isCompleted,
          isFocusNode,
          // Check if this is a root node (no incoming edges)
          isRootNode: !edges.some(e => e.target === node.id),
          expanded: expandedNodes.has(node.id),
          onToggleExpand: () => toggleNodeExpansion(node.id),
          // Always use BT layout direction
          layoutDirection: 'BT',
          // For directory nodes, pass the child count
          childCount: isDirectory ? node.children?.length || 0 : undefined,
          // Add hydration status information
          hydrationStatus: status,
          inProgress: currentlyHydrating === node.id,
          hasError: failedNodes && failedNodes.has(node.id),
          isInHydrationPlan
        },
        // Add opacity for nodes not in hydration plan
        style: !isInHydrationPlan ? { opacity: 0.7 } : undefined
      });
    });
    
    // Create a map of all nodes for quick lookup
    const allManifestNodes = manifest?.nodes || {};
    
    // Process edges (dependencies)
    // First, collect all nodes that are in the current view
    const nodeIdsInView = new Set(nodes.map(n => n.id));
    
    console.log("=== EDGE CREATION DEBUG ===");
    console.log("Total nodes created:", nodes.length);
    console.log("Node IDs in view:", Array.from(nodeIdsInView));
    
    let edgeCreationAttempts = 0;
    let successfulEdges = 0;
        
    // For root node view, we need to explicitly create edges between all nodes based on their dependencies
    if (!focusNodeId && manifest?.nodes) {
      console.log("Root node view - forcing connections between nodes");
      
      // Create a map of all nodes for quick lookup
      const nodeMap = new Map();
      nodes.forEach(node => nodeMap.set(node.id, node));
      
      // Iterate through all nodes in the manifest
      Object.entries(manifest.nodes).forEach(([nodeId, nodeData]) => {
        // Skip if not a file or if it doesn't have dependencies
        if (nodeData.type !== 'file' || !nodeData.dependencies || nodeData.dependencies.length === 0) {
          return;
        }
        
        // Check if this node is in our view
        if (!nodeMap.has(nodeId)) {
          return;
        }
        
        // Process each dependency
        nodeData.dependencies.forEach(depPath => {
          edgeCreationAttempts++;
          
          // Handle both string paths and object dependencies
          const depId = typeof depPath === 'object' ? depPath.id : depPath;
          
          // If the dependency is a path (string that includes '/'), resolve it to a node ID
          let resolvedDepId = depId;
          if (typeof depId === 'string' && depId.includes('/')) {
            // Try to find the node with this path
            for (const [id, node] of Object.entries(manifest.nodes)) {
              if (node.path === depId || node.path === depId.replace(/^\//, '')) {
                resolvedDepId = id;
                break;
              }
            }
          }
          
          // Skip if dependency is not a file or not in our view
          const depNode = manifest.nodes[resolvedDepId];
          if (!depNode || depNode.type !== 'file' || !nodeMap.has(resolvedDepId)) {
            console.log(`Skipping edge ${nodeId} -> ${resolvedDepId}: dep not in view or not a file`);
            return;
          }
        
          // Create an edge between the nodes
          edges.push({
            id: `${nodeId}-${resolvedDepId}`,
            source: nodeId,
            target: resolvedDepId,
            animated: false,
            style: { 
              stroke: getEdgeColor(nodeData.status, darkMode),
              strokeWidth: 2,
              opacity: 0.9,
              transition: 'stroke 0.3s, strokeWidth 0.3s, opacity 0.3s'
            },
            type: 'custom',
            markerEnd: {
              type: 'arrow',
              width: 15,
              height: 15,
              color: getEdgeColor(nodeData.status, darkMode)
            },
            data: {
              status: nodeData.status,
              sourceStatus: nodeData.status,
              targetStatus: depNode.status,
              isInHydrationPlan: true
            }
          });
        });
      });
    }
    
    // Process edges for all nodes in the view
    nodesToProcess.forEach((node: any) => {
      // Skip directory nodes when in root node view
      if (!focusNodeId && node.type === 'directory') return;
          
      const dependencies = node.dependencies || [];
      dependencies.forEach((dep: any) => {
        // Handle both object format and string format dependencies
        const depId = typeof dep === 'object' ? dep.id : dep;
          
        // If the dependency is a path (string that includes '/'), resolve it to a node ID
        let resolvedDepId = depId;
        if (typeof depId === 'string' && depId.includes('/')) {
          // Try to find the node with this path
          for (const [id, node] of Object.entries(allManifestNodes)) {
            if (node.path === depId || node.path === depId.replace(/^\//, '')) {
              resolvedDepId = id;
              break;
            }
          }
        }
            
        // Get the target node from the manifest
        const depNode = allManifestNodes[resolvedDepId];
            
        // Skip if the dependency node doesn't exist in the manifest
        if (!depNode) return;
            
        // Skip directory dependencies when in root node view
        if (!focusNodeId && depNode.type === 'directory') return;
            
        // In root node view, only include file-to-file dependencies
        if (!focusNodeId) {
          // Only add edge if both source and target nodes are in the current view
          if (nodeIdsInView.has(node.id) && nodeIdsInView.has(resolvedDepId)) {
            edges.push({
              id: `${node.id}-${resolvedDepId}`,
              source: node.id,
              target: resolvedDepId,
              animated: node.status === 'hydrating' || depNode.status === 'hydrating',
              style: { 
                stroke: getEdgeColor(node.status, darkMode),
                strokeWidth: 2,
                opacity: 0.9, // Higher opacity for all edges in root view
                transition: 'stroke 0.3s, strokeWidth 0.3s, opacity 0.3s'
              },
              type: 'custom',
              markerEnd: {
                type: 'arrow',
                width: 15,
                height: 15,
                color: getEdgeColor(node.status, darkMode)
              },
              data: {
                status: node.status,
                sourceStatus: node.status,
                targetStatus: depNode.status,
                isInHydrationPlan: true
              }
            });
          }
          return;
        }
            
        // If we're not showing directories and the dependency is a directory, skip it
        if (!showAllDirectories && depNode.type === 'directory') return;
            
        // If the dependency node is not in the current view but exists in the manifest,
        // we need to check if it's a directory
        if (!nodeIdsInView.has(resolvedDepId)) {
          // If it's a directory and we're not showing directories, skip this edge
          if (depNode.type === 'directory' && !showAllDirectories) return;
              
          // If it's a file but we're not showing all nodes and it's not in the hydration plan, skip
          if (depNode.type !== 'directory' && !showAllNodes && 
              (!hydrationPlan?.ordered_plan || !hydrationPlan.ordered_plan.some(n => n.id === resolvedDepId))) {
            return;
          }
        }
            
        // Get the current status of the dependency node
        let depStatus = 'ghost';
            
        if (completedNodes.has(resolvedDepId)) {
          depStatus = 'fresh';
        } else if (currentlyHydrating === resolvedDepId) {
          depStatus = 'hydrating';
        } else if (nodeStatuses[resolvedDepId]) {
          depStatus = nodeStatuses[resolvedDepId];
        } else if (depNode) {
          depStatus = depNode.status;
        }
            
        // Only add edge if both source and target nodes are in the current view
        if (nodeIdsInView.has(node.id) && nodeIdsInView.has(resolvedDepId)) {
          const isFocusNode = node.id === focusNodeId;
          // For root node view, consider all nodes to be in the hydration plan
          const isInHydrationPlan = !focusNodeId || !showAllNodes || 
            (hydrationPlan?.ordered_plan && hydrationPlan.ordered_plan.some(n => n.id === node.id));
              
          edges.push({
            id: `${node.id}-${resolvedDepId}`,
            source: node.id,
            target: resolvedDepId,
            animated: node.status === 'hydrating' || depStatus === 'hydrating',
            style: { 
              stroke: getEdgeColor(node.status, darkMode),
              strokeWidth: isFocusNode || node.id === focusNodeId ? 3 : 2,
              strokeDasharray: node.status === 'ghost' ? '5,5' : undefined,
              // In root node view, always show edges with higher opacity
              opacity: !focusNodeId || isInHydrationPlan ? 0.9 : 0.5,
              transition: 'stroke 0.3s, strokeWidth 0.3s, opacity 0.3s',
              // Add glow effect for dependencies
              filter: isFocusNode || node.id === focusNodeId ? 
                `drop-shadow(0 0 3px ${getEdgeColor(node.status, darkMode)})` : 
                undefined
            },
            type: 'custom', // Use our custom edge type
            // Add custom edge properties
            markerEnd: {
              type: 'arrow',
              width: 15,
              height: 15,
              color: getEdgeColor(node.status, darkMode)
            },
            // Add custom edge data
            data: {
              status: node.status,
              sourceStatus: node.status,
              targetStatus: depStatus,
              isInHydrationPlan
            }
          });
          successfulEdges++;
        }
      });
    });
    
    console.log("Edge creation attempts:", edgeCreationAttempts);
    console.log("Successful edges created:", successfulEdges);
    console.log("Final edges:", edges.length);
    
    // If we're not showing directories, create direct edges between files
    // that would be connected through directories
    if (!showAllDirectories) {
      // Create a map to track directory dependencies
      const dirDependencies = new Map<string, Set<string>>();
      const dirDependents = new Map<string, Set<string>>();
      
      // Identify all directory nodes and their dependencies/dependents
      Object.entries(allManifestNodes).forEach(([nodeId, node]) => {
        if (node.type === 'directory') {
          // For each directory, find files that depend on it
          Object.entries(allManifestNodes).forEach(([fileId, fileNode]) => {
            if (fileNode.type === 'file' && fileNode.dependencies) {
              if (fileNode.dependencies.includes(nodeId)) {
                // This file depends on the directory
                if (!dirDependents.has(nodeId)) {
                  dirDependents.set(nodeId, new Set<string>());
                }
                dirDependents.get(nodeId)!.add(fileId);
                
                // Also track which directories each file depends on
                if (!dirDependencies.has(fileId)) {
                  dirDependencies.set(fileId, new Set<string>());
                }
                dirDependencies.get(fileId)!.add(nodeId);
              }
            }
          });
        }
      });
      
      // Instead of creating bidirectional edges between all files that share a directory,
      // create a more hierarchical structure that better represents the actual dependencies
      dirDependents.forEach((dependentFiles, dirId) => {
        // Get all files that depend on this directory
        const filesArray = Array.from(dependentFiles);
        
        // If there are multiple files depending on this directory
        if (filesArray.length > 1) {
          // Sort files by name for consistent ordering
          filesArray.sort((a, b) => {
            const nameA = allManifestNodes[a]?.path?.toLowerCase() || '';
            const nameB = allManifestNodes[b]?.path?.toLowerCase() || '';
            return nameA.localeCompare(nameB);
          });
          
          // Create a chain of dependencies instead of a fully connected graph
          for (let i = 0; i < filesArray.length - 1; i++) {
            const fileA = filesArray[i];
            const fileB = filesArray[i + 1];
            
            // Only create edges if both files are in the current view
            if (nodeIdsInView.has(fileA) && nodeIdsInView.has(fileB)) {
              const fileANode = allManifestNodes[fileA];
              const fileBNode = allManifestNodes[fileB];
              
              if (fileANode && fileBNode) {
                // Create a single edge from A to B
                edges.push({
                  id: `${fileA}-${fileB}-via-dir-${dirId}`,
                  source: fileA,
                  target: fileB,
                  animated: false,
                  style: { 
                    stroke: getEdgeColor(fileANode.status, darkMode),
                    strokeWidth: 1.5,
                    strokeDasharray: '5,5', // Dashed line to indicate derived connection
                    opacity: 0.7,
                    transition: 'stroke 0.3s, strokeWidth 0.3s, opacity 0.3s'
                  },
                  type: 'custom',
                  markerEnd: {
                    type: 'arrow',
                    width: 12,
                    height: 12,
                    color: getEdgeColor(fileANode.status, darkMode)
                  },
                  data: {
                    isDerivedConnection: true,
                    viaDirectory: dirId,
                    status: fileANode.status,
                    sourceStatus: fileANode.status,
                    targetStatus: fileBNode.status
                  }
                });
              }
            }
          }
        }
      });
    }
    
    return { initialNodes: nodes, initialEdges: edges };
  }, [focusNodeId, manifest, nodeStatuses, completedNodes, hydrationPlan, expandedNodes, darkMode, showAllNodes, showAllDirectories]);
  
  // Use React Flow hooks to manage nodes and edges
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  
  // Get React Flow instance for operations like fitView
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { fitView, zoomIn, zoomOut, setCenter } = useReactFlow();
  
  // Track component mounting state
  const isComponentMounted = useRef(true);
  
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);
  
  // Reset focus progress flag whenever focusNodeId changes
  useEffect(() => {
    focusInProgress.current = false;
    console.log(`Focus node changed to: ${focusNodeId}, resetting focus progress flag`);
    
    // Also reset user focus state when focus node changes
    setUserHasFocused(false);
  }, [focusNodeId]);

  // Add refs to track rendering and layout state
  const focusInProgress = useRef(false);
  const layoutOperationRef = useRef(false);
  
  // Add state to track if user has manually focused on a node
  const [userHasFocused, setUserHasFocused] = useState(false);
  const [isCalculatingLayout, setIsCalculatingLayout] = useState(false);

  // Highlight node and its dependencies with debouncing - memoize with empty dependency array
  const highlightNodeWithDependencies = useCallback(
    debounce((nodeId: string) => {
      if (focusInProgress.current) return;
      
      // Find all dependencies
      const dependencies = findNodeDependencies(nodeId, edges);
      
      // Update nodes with highlighting
      setNodes(prevNodes => prevNodes.map(node => ({
        ...node,
        style: {
          ...node.style,
          opacity: node.id === nodeId || dependencies.has(node.id) ? 1 : 0.3
        }
      })));
      
      // Update edges with highlighting
      setEdges(prevEdges => prevEdges.map(edge => ({
        ...edge,
        style: {
          ...edge.style,
          opacity: edge.source === nodeId || dependencies.has(edge.source) ? 1 : 0.3
        }
      })));
    }, 300),
    [] // Empty dependency array for stability
  );

  // Reset highlighting with debouncing - memoize with empty dependency array
  const resetHighlighting = useCallback(
    debounce(() => {
      if (focusInProgress.current) return;
      
      // Only reset highlighting, but don't refocus on the primary node
      // if the user has manually focused somewhere
      
      // Reset all nodes and edges to full opacity
      setNodes(prevNodes => prevNodes.map(node => ({
        ...node,
        style: {
          ...node.style,
          opacity: 1
        }
      })));
      
      setEdges(prevEdges => prevEdges.map(edge => ({
        ...edge,
        style: {
          ...edge.style,
          opacity: 1
        }
      })));
    }, 300),
    [] // Empty dependency array for stability
  );
  
  // Create a stable flow key that only changes when necessary
  const flowKey = useMemo(() => 
    `flow-${initialNodes.length}-${darkMode}-${focusNodeId || 'none'}`,
    [initialNodes.length, darkMode, focusNodeId]
  );

  // Check manifest dependency data at component initialization
  useEffect(() => {
    console.log("=== MANIFEST DEPENDENCY CHECK ===");
    if (manifest && manifest.nodes) {
      let nodesWithDeps = 0;
      let totalDeps = 0;
      let nodesWithStringDeps = 0;
      let nodesWithArrayDeps = 0;
      
      Object.values(manifest.nodes).forEach((node: any) => {
        if (node.dependencies && node.dependencies.length > 0) {
          nodesWithDeps++;
          totalDeps += node.dependencies.length;
          
          if (typeof node.dependencies[0] === 'string') {
            nodesWithStringDeps++;
          } else if (typeof node.dependencies[0] === 'object') {
            nodesWithArrayDeps++;
          }
          
          console.log(`Node ${node.id} (${node.path}) has ${node.dependencies.length} dependencies`);
        }
      });
      
      console.log("Nodes with dependencies:", nodesWithDeps);
      console.log("Total dependencies:", totalDeps);
      console.log("Nodes with string dependencies:", nodesWithStringDeps);
      console.log("Nodes with object dependencies:", nodesWithArrayDeps);
    }
  }, [manifest]);

  // Apply automatic layout with simplified dependencies
  useEffect(() => {
    // Skip if component is unmounting or layout operation is in progress
    if (!isComponentMounted.current || layoutOperationRef.current) return;
    
    console.log("=== LAYOUT DEBUG ===");
    console.log("Layout effect triggered - initialNodes:", initialNodes.length, "prevNodeCount:", prevNodeCount.current);
    
    // Only apply layout when nodes or edges change significantly
    // Not on every render or selection change
    if (initialNodes.length > 0 && (
      // Only re-layout when the node count changes or we're initializing
      prevNodeCount.current !== initialNodes.length || 
      initialLayoutApplied.current === false
    )) {
      // Set layout operation in progress to prevent concurrent layouts
      layoutOperationRef.current = true;
      setIsCalculatingLayout(true);
      
      console.log("Applying layout to", initialNodes.length, "nodes");
      
      // Use setTimeout to ensure this doesn't block rendering
      setTimeout(() => {
        try {
          console.log("Applying layout to nodes:", initialNodes.length);
          console.log("With edges:", initialEdges.length);
            
          // Analyze the dependency structure
          const nodeGroups = groupNodesByDAG(initialNodes, initialEdges);
          const rootNodes = findRootNodes(initialNodes, initialEdges);
          const isSimpleStructure = rootNodes.length === initialNodes.length; // All nodes are root nodes (no dependencies)
            
          console.log("Root nodes:", rootNodes.length);
          console.log("Is simple structure:", isSimpleStructure);
          
          // Determine if we have a flat structure (all nodes at same level)
          const hasDependencies = initialEdges.length > 0;
          
          let layoutedElements;
          const direction = 'BT'; // Always use Bottom to Top
          
          if (isSimpleStructure || !hasDependencies) {
            // For simple structures with no dependencies, arrange horizontally
            const horizontalSpacing = 220; // Space between nodes
            const startX = 50; // Starting X position
            const baseY = 100; // Base Y position for all nodes
            
            // Sort nodes by name for consistent ordering
            const sortedNodes = [...initialNodes].sort((a, b) => {
              const nameA = a.data.label.toLowerCase();
              const nameB = b.data.label.toLowerCase();
              return nameA.localeCompare(nameB);
            });
            
            // Position nodes horizontally
            const updatedNodes = sortedNodes.map((node, index) => ({
              ...node,
              position: {
                x: startX + index * horizontalSpacing,
                y: baseY
              },
              data: {
                ...node.data,
                layoutDirection: direction
              }
            }));
            
            layoutedElements = { nodes: updatedNodes, edges: initialEdges };
          } else {
            // For complex structures with dependencies, use dagre layout
            layoutedElements = getLayoutedElements(initialNodes, initialEdges, 'BT', !focusNodeId);
          }
          
          // For root node view with dependencies, ensure edges are properly visible
          if (!focusNodeId && initialEdges.length > 0) {
            console.log("Root node view with dependencies - ensuring connections are visible");
            console.log("Initial edges count:", initialEdges.length);
            
            // Force edges to be visible by setting higher opacity
            const updatedEdges = initialEdges.map(edge => ({
              ...edge,
              style: {
                ...edge.style,
                opacity: 0.9, // Higher opacity for all edges in root view
                strokeWidth: 2  // Consistent stroke width
              }
            }));
            
            layoutedElements = {
              nodes: layoutedElements.nodes,
              edges: updatedEdges
            };
          }
          
          // Update the nodes with the layout direction
          const updatedNodes = layoutedElements.nodes.map(node => ({
            ...node,
            data: {
              ...node.data,
              layoutDirection: direction
            }
          }));
          
          // Explicitly set source and target handles based on layout direction
          const updatedEdges = initialEdges.map(edge => ({
            ...edge,
            // Force React Flow to recalculate the edge path by adding a key based on the layout
            id: `${edge.source}-${edge.target}-${direction}`,
            // Explicitly set source and target handles based on layout direction
            sourceHandle: 'top',
            targetHandle: 'bottom'
          }));
          
          if (isComponentMounted.current) {
            // Apply the layout with a slight delay to ensure state is stable
            setTimeout(() => {
              if (!isComponentMounted.current) return;
        
              console.log("Layout calculated with nodes:", updatedNodes.length);
              console.log("And edges:", updatedEdges.length);
        
              setNodes(updatedNodes);
              setEdges(updatedEdges);
        
              // Apply enhanced group styling based on rootNodeId in node data
              const rootNodeGroups = new Map<string, string[]>();
              updatedNodes.forEach(node => {
                if (node.data.rootNodeId) {
                  if (!rootNodeGroups.has(node.data.rootNodeId)) {
                    rootNodeGroups.set(node.data.rootNodeId, []);
                  }
                  rootNodeGroups.get(node.data.rootNodeId)?.push(node.id);
                }
              });
        
              // Apply group styling if we have multiple groups
              if (rootNodeGroups.size > 1) {
                const groupColors = [
                  'rgba(59, 130, 246, 0.1)',  // blue
                  'rgba(16, 185, 129, 0.1)',  // green
                  'rgba(249, 115, 22, 0.1)',  // orange
                  'rgba(139, 92, 246, 0.1)',  // purple
                  'rgba(236, 72, 153, 0.1)'   // pink
                ];
          
                setNodes(updatedNodes.map(node => {
                  if (node.data.rootNodeId) {
                    const groupIndex = Array.from(rootNodeGroups.keys()).indexOf(node.data.rootNodeId);
                    if (groupIndex >= 0) {
                      return {
                        ...node,
                        style: {
                          ...node.style,
                          backgroundColor: groupColors[groupIndex % groupColors.length],
                          // Make root nodes more prominent
                          ...(node.id === node.data.rootNodeId ? {
                            borderWidth: 3,
                            borderColor: groupColors[groupIndex % groupColors.length].replace('0.1', '0.8')
                          } : {})
                        }
                      };
                    }
                  }
                  return node;
                }));
          
                // Also style edges based on their group
                setEdges(updatedEdges.map(edge => {
                  if (edge.data?.rootNodeId) {
                    const groupIndex = Array.from(rootNodeGroups.keys()).indexOf(edge.data.rootNodeId);
                    if (groupIndex >= 0) {
                      return {
                        ...edge,
                        style: {
                          ...edge.style,
                          // Add subtle group-specific styling
                          opacity: 0.8,
                          // Make edges from root nodes more prominent
                          strokeWidth: edge.source === edge.data.rootNodeId ? 3 : edge.style?.strokeWidth || 2
                        }
                      };
                    }
                  }
                  return edge;
                }));
              } else {
                // If only one group, still apply styling to root nodes
                setNodes(updatedNodes.map(node => {
                  if (node.data.rootNodeId && node.id === node.data.rootNodeId) {
                    return {
                      ...node,
                      style: {
                        ...node.style,
                        borderWidth: 3,
                        borderColor: 'rgba(59, 130, 246, 0.8)'
                      }
                    };
                  }
                  return node;
                }));
              }
              
              // Always fit view after layout is applied
              setTimeout(() => {
                if (isComponentMounted.current) {
                  // First apply all layout changes to nodes and edges
                  setNodes(updatedNodes);
                  setEdges(updatedEdges);
                  
                  // IMPORTANT: Only call fitView as the ABSOLUTE LAST operation
                  // with enough delay to ensure all other state updates have completed
                  setTimeout(() => {
                    if (isComponentMounted.current) {
                      console.log("Final fitView call - should be the last operation");
                      fitView({ padding: 0.2 });
                    }
                    
                    // Only update these flags AFTER fitView has been called
                    initialLayoutApplied.current = true;
                    prevNodeCount.current = initialNodes.length;
                    setIsCalculatingLayout(false);
                    layoutOperationRef.current = false;
                  }, 150); // Use a longer timeout to ensure everything else is done
                } else {
                  // If component is unmounted, just clean up
                  prevNodeCount.current = initialNodes.length;
                  setIsCalculatingLayout(false);
                  layoutOperationRef.current = false;
                }
              }, 100);
            }, 50);
          } else {
            // Release locks if component is no longer mounted
            setIsCalculatingLayout(false);
            layoutOperationRef.current = false;
          }
        } catch (error) {
          console.error("Error during layout calculation:", error);
          setIsCalculatingLayout(false);
          layoutOperationRef.current = false;
        }
      }, 0);
    }
  }, [initialNodes.length, initialEdges.length, focusNodeId]); // Simplified dependencies
  
  // Node selection handler with focus management
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (focusInProgress.current) return;
    focusInProgress.current = true;
    
    // Set flag that user has manually focused
    setUserHasFocused(true);
    
    // Highlight the node and its dependencies
    highlightNodeWithDependencies(node.id);
    
    // DON'T call fitView here - let the layout effect handle it
    // This prevents multiple competing fitView operations
    
    // Reset the focus flag after animation completes
    setTimeout(() => {
      focusInProgress.current = false;
    }, 300);
    
  }, [highlightNodeWithDependencies]);
  
  // Toggle node expansion
  const toggleNodeExpansion = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);
  // Reset layout when showAllNodes or showAllDirectories changes
  useEffect(() => {
    console.log("Toggle state changed - resetting graph layout");
    // Reset layout flags to force re-layout
    initialLayoutApplied.current = false;
    prevNodeCount.current = 0;
    
    // Use a ref to prevent multiple executions in the same render cycle
    const timeoutId = setTimeout(() => {
      if (!isComponentMounted.current) return;
      
      // Force a complete re-render of the nodes and edges
      // But use initialNodes/Edges directly to avoid dependency cycles
      const layoutedElements = getLayoutedElements(initialNodes, initialEdges, 'BT');
      
      // Update the nodes with the layout direction
      const updatedNodes = layoutedElements.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          layoutDirection: 'BT'
        }
      }));
      
      // Explicitly set source and target handles based on layout direction
      const updatedEdges = initialEdges.map(edge => ({
        ...edge,
        // Force React Flow to recalculate the edge path by adding a key based on the layout
        id: `${edge.source}-${edge.target}-BT`,
        // Explicitly set source and target handles based on layout direction
        sourceHandle: 'top',
        targetHandle: 'bottom'
      }));
      
      if (isComponentMounted.current) {
        setNodes(updatedNodes);
        setEdges(updatedEdges);
        
        // Apply group styling if we have multiple DAGs
        const nodeGroups = groupNodesByDAG(updatedNodes, updatedEdges);
        if (Object.keys(nodeGroups).length > 1) {
          if (isComponentMounted.current) {
            setNodes(applyGroupStyling(updatedNodes, nodeGroups));
          }
        }
        
        // Add a small delay to ensure state updates before layout
        setTimeout(() => {
          if (isComponentMounted.current && fitView) {
            fitView({ padding: 0.2 });
          }
        }, 100);
      }
    }, 50);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [showAllNodes, showAllDirectories]); // Remove initialNodes, initialEdges dependencies
  
  // Listen for force refresh events
  useEffect(() => {
    const handleForceRefresh = () => {
      console.log("Force refresh event received");
      initialLayoutApplied.current = false;
      prevNodeCount.current = 0;
      
      // Create a local variable to track if the effect is still active
      let isActive = true;
      
      // Force a complete re-render with a slight delay
      const timeoutId = setTimeout(() => {
        if (isActive && initialNodes.length > 0 && isComponentMounted.current) {
          // Apply layout directly instead of just setting nodes/edges
          const layoutedElements = getLayoutedElements(initialNodes, initialEdges, 'BT');
          
          // Update the nodes with the layout direction
          const updatedNodes = layoutedElements.nodes.map(node => ({
            ...node,
            data: {
              ...node.data,
              layoutDirection: 'BT'
            }
          }));
          
          // Explicitly set source and target handles based on layout direction
          const updatedEdges = initialEdges.map(edge => ({
            ...edge,
            id: `${edge.source}-${edge.target}-BT`,
            sourceHandle: 'top',
            targetHandle: 'bottom'
          }));
          
          if (isComponentMounted.current) {
            setNodes(updatedNodes);
            setEdges(updatedEdges);
            
            setTimeout(() => {
              if (isActive && isComponentMounted.current && fitView) {
                fitView({ padding: 0.2 });
              }
            }, 50);
          }
        }
      }, 50);
      
      return () => {
        isActive = false;
        clearTimeout(timeoutId);
      };
    };
    
    window.addEventListener('forceGraphRefresh', handleForceRefresh);
    return () => {
      window.removeEventListener('forceGraphRefresh', handleForceRefresh);
    };
  }, [initialNodes, initialEdges]); // Only depend on initialNodes and initialEdges
  
  // Add an effect to respond to hydration state changes
  useEffect(() => {
    // Skip if component is unmounting or no nodes to update
    if (!isComponentMounted.current || nodes.length === 0) return;
    
    console.log("Hydration state changed - updating graph");
    
    // Create a copy of the current nodes to avoid modifying state directly
    const nodesToUpdate = [...nodes];
    
    // Track if we need to update state
    let hasNodeChanges = false;
    
    // Update node statuses based on hydration state
    const updatedNodes = nodesToUpdate.map(node => {
      // Check if this node's status has changed
      let newStatus = node.data.status;
      
      if (completedNodes.has(node.id)) {
        newStatus = 'fresh';
      } else if (currentlyHydrating === node.id) {
        newStatus = 'hydrating';
      } else if (nodeStatuses[node.id]) {
        newStatus = nodeStatuses[node.id];
      }
      
      // Only update if the status has changed
      if (newStatus !== node.data.status || 
          currentlyHydrating === node.id !== node.data.inProgress ||
          (failedNodes && failedNodes.has(node.id)) !== node.data.hasError) {
        hasNodeChanges = true;
        return {
          ...node,
          data: {
            ...node.data,
            status: newStatus,
            hydrationStatus: newStatus,
            inProgress: currentlyHydrating === node.id,
            hasError: failedNodes && failedNodes.has(node.id)
          }
        };
      }
      return node;
    });
    
    // Create a copy of the current edges to avoid modifying state directly
    const edgesToUpdate = [...edges];
    
    // Track if we need to update edges
    let hasEdgeChanges = false;
    
    // Update edges based on new node statuses
    const updatedEdges = edgesToUpdate.map(edge => {
      const sourceNode = updatedNodes.find(n => n.id === edge.source);
      const targetNode = updatedNodes.find(n => n.id === edge.target);
      
      if (sourceNode && targetNode) {
        const isAnimated = sourceNode.data.status === 'hydrating' || targetNode.data.status === 'hydrating';
        const newStroke = getEdgeColor(sourceNode.data.status, darkMode);
        const newStrokeWidth = sourceNode.data.isFocusNode || targetNode.data.isFocusNode ? 3 : 2;
        const newOpacity = sourceNode.data.isInHydrationPlan ? 0.9 : 0.5;
        
        // Only update if something has changed
        if (edge.animated !== isAnimated || 
            edge.style?.stroke !== newStroke ||
            edge.style?.strokeWidth !== newStrokeWidth ||
            edge.style?.opacity !== newOpacity ||
            edge.data?.status !== sourceNode.data.status) {
          
          hasEdgeChanges = true;
          return {
            ...edge,
            animated: isAnimated,
            style: {
              ...edge.style,
              stroke: newStroke,
              strokeWidth: newStrokeWidth,
              opacity: newOpacity
            },
            data: {
              ...edge.data,
              status: sourceNode.data.status,
              sourceStatus: sourceNode.data.status,
              targetStatus: targetNode.data.status
            }
          };
        }
      }
      return edge;
    });
    
    // Only update state if there are actual changes and component is still mounted
    if (hasNodeChanges && isComponentMounted.current) {
      setNodes(updatedNodes);
    }
    
    if (hasEdgeChanges && isComponentMounted.current) {
      setEdges(updatedEdges);
    }
  }, [completedNodes, currentlyHydrating, nodeStatuses, failedNodes, darkMode]);
  
  // Create a function to focus on a specific node with debouncing
  const focusOnNode = useCallback(
    debounce((nodeId: string) => {
      if (focusInProgress.current) return;
      focusInProgress.current = true;
      
      // Set flag that user has manually focused when using the API
      setUserHasFocused(true);
      
      // Find the node in our nodes array
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        console.log(`Highlighting node: ${nodeId}`);
        
        // Highlight the node and its dependencies
        highlightNodeWithDependencies(nodeId);
        
        // Instead of zooming to the node, fit the entire view
        setTimeout(() => {
          if (isComponentMounted.current && fitView) {
            fitView({ padding: 0.2 });
          }
          
          setTimeout(() => {
            focusInProgress.current = false;
          }, 300);
        }, 50); // Small delay to ensure the node positions are updated
      } else {
        console.warn(`Node not found for focusing: ${nodeId}`);
        focusInProgress.current = false;
      }
    }, 300),
    [nodes, highlightNodeWithDependencies, fitView]
  );

  // Register the focus callback when the component mounts with focus management
  useEffect(() => {
    if (onFocusNode) {
      onFocusNode(focusOnNode);
    }
    
    // ALWAYS focus on the focus node if one is provided, regardless of layout state
    if (focusNodeId && nodes.length > 0 && !focusInProgress.current) {
      console.log(`FOCUSING ON NODE: ${focusNodeId} - THIS SHOULD ALWAYS HAPPEN`);
      focusInProgress.current = true;
      
      // Use a longer delay to ensure layout is fully complete
      setTimeout(() => {
        // First highlight the node and its dependencies
        highlightNodeWithDependencies(focusNodeId);
        
        // Then explicitly center on the node
        const nodeToFocus = nodes.find(n => n.id === focusNodeId);
        if (nodeToFocus) {
          console.log(`Found focus node at position:`, nodeToFocus.position);
          
          // First fit view to show everything
          fitView({ padding: 0.2, duration: 0 });
          
          // Then after a short delay, center on the specific node
          setTimeout(() => {
            console.log(`Centering on focus node: ${focusNodeId}`);
            // Use a more moderate zoom level - halfway between current view and full zoom
            const zoom = 1.2; // Reduced from 1.5 for a less aggressive zoom
            setCenter(nodeToFocus.position.x, nodeToFocus.position.y, { 
              zoom, 
              duration: 800 // Increased from 300 to 800 for a more noticeable animation
            });
            
            setTimeout(() => {
              focusInProgress.current = false;
            }, 900); // Increased to match the longer animation duration
          }, 100);
        } else {
          console.log(`Focus node not found in nodes array: ${focusNodeId}`);
          focusInProgress.current = false;
        }
      }, 300); // Longer delay to ensure layout is complete
    }
  }, [onFocusNode, focusOnNode, focusNodeId, nodes, highlightNodeWithDependencies, fitView, setCenter]);
  
  // Add state for hover card with delay handling
  const [hoverNode, setHoverNode] = useState<Node | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number, y: number } | null>(null);
  const [isHoverVisible, setIsHoverVisible] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Add state to store content previews
  const [contentPreviews, setContentPreviews] = useState<Record<string, string>>({});
  
  // Add function to fetch content preview
  const fetchContentPreview = useCallback(async (nodeId: string, artifactId: string) => {
    // Skip if we already have the content
    if (contentPreviews[nodeId]) return;
    
    try {
      // Fetch the content from the artifact API
      const response = await api.get(`/artifacts/${artifactId}`);
      if (response.data && response.data.content) {
        // Get first 20 lines
        const preview = response.data.content.split('\n').slice(0, 20).join('\n');
        setContentPreviews(prev => ({
          ...prev,
          [nodeId]: preview
        }));
      }
    } catch (error) {
      console.error("Error fetching content preview:", error);
    }
  }, [contentPreviews]);
  
  // Add handlers for node mouse events with debouncing
  const onNodeMouseEnter = useCallback((event: React.MouseEvent, node: Node) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Get the position of the node in the viewport
    const rect = event.currentTarget.getBoundingClientRect();
    const x = rect.left - 5; // Position left next to the node with minimal spacing
    const y = rect.top + (rect.height / 2); // Align with the middle of the node
    
    // Set the node and position immediately
    setHoverNode(node);
    setHoverPosition({ x, y });
    
    // Fetch content preview if needed
    if (node.data.status === 'fresh' && manifest?.nodes[node.id]?.artifact_id && !contentPreviews[node.id]) {
      fetchContentPreview(node.id, manifest.nodes[node.id].artifact_id);
    }
    
    // Set a timeout to show the hover card after a short delay
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHoverVisible(true);
    }, 300); // 300ms delay before showing
  }, [manifest, contentPreviews, fetchContentPreview]);
  
  const onNodeMouseLeave = useCallback(() => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // Set a timeout to hide the hover card after a short delay
    // This prevents flickering when moving between nodes
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHoverVisible(false);
      // Only reset the node and position after the card has disappeared
      setTimeout(() => {
        setHoverNode(null);
        setHoverPosition(null);
      }, 200);
    }, 100);
  }, []);
  
  // Clean up timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (containerRef.current) {
      if (!isFullscreen) {
        if (containerRef.current.requestFullscreen) {
          containerRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
      setIsFullscreen(!isFullscreen);
    }
  }, [isFullscreen]);
  
  // Filter nodes by status
  const filteredNodes = useMemo(() => {
    if (statusFilter.length === 0) return nodes;
    return nodes.filter(node => statusFilter.includes(node.data.status));
  }, [nodes, statusFilter]);
  
  // Search nodes
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    return nodes.filter(node => 
      node.data.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.data.path.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [nodes, searchTerm]);
  
  // Focus on search result
  const focusOnSearchResult = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setCenter(node.position.x, node.position.y, { zoom: 1.8, duration: 800 });
      highlightNodeWithDependencies(nodeId);
    }
  }, [nodes, setCenter, highlightNodeWithDependencies]);
  
  // Stats for the visualization
  const stats = useMemo(() => {
    const nodeGroups = groupNodesByDAG(nodes, edges);
    
    // Count directories and files separately
    const dirCount = nodes.filter(node => node.type === 'directory').length;
    const fileCount = nodes.length - dirCount;
    
    return {
      totalNodes: nodes.length,
      fileCount,
      dirCount,
      byStatus: nodes.reduce((acc: Record<string, number>, node) => {
        const status = node.data.status;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
      dagCount: Object.keys(nodeGroups).length
    };
  }, [nodes, edges]);

  // If no manifest or no nodes to display, show a message
  if (!manifest?.nodes || initialNodes.length === 0) {
    return (
      <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="text-sm font-medium mb-2">Dependency Graph</div>
        <div 
          className={`p-3 rounded-md ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} overflow-auto text-center`} 
          style={{ maxHeight }}
        >
          No dependencies to display
        </div>
      </div>
    );
  }

  return (
    <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className="text-sm font-medium mb-2 flex justify-between items-center">
        <span>Dependency Graph</span>
        <div className="flex items-center space-x-2">
          {/* Status filter dropdown */}
          <div className="relative">
            <button
              className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              onClick={() => setStatusFilter(prev => prev.length ? [] : ['ghost', 'stale'])}
              title="Filter by status"
            >
              <FiFilter className={statusFilter.length ? 'text-blue-500' : ''} />
            </button>
          </div>
          
          {/* Search input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`text-xs border rounded p-1 w-24 ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            />
            {searchResults.length > 0 && (
              <div className={`absolute z-10 mt-1 w-48 rounded-md shadow-lg ${
                darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
                <ul className="py-1 max-h-48 overflow-auto">
                  {searchResults.map(node => (
                    <li 
                      key={node.id}
                      className={`px-2 py-1 text-xs cursor-pointer ${
                        darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      }`}
                      onClick={() => focusOnSearchResult(node.id)}
                    >
                      {node.data.label} <span className="opacity-60">({node.data.path})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {/* Fullscreen toggle */}
          <button
            className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            <FiMaximize />
          </button>
        </div>
      </div>
      
      {/* Stats bar */}
      <div className={`mb-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {stats.totalNodes} nodes in {stats.dagCount} groups
        {stats.dirCount > 0 && ` (${stats.fileCount} files, ${stats.dirCount} directories)`} • 
        {Object.entries(stats.byStatus).map(([status, count]) => (
          <span key={status} className="ml-2">
            <span className={getStatusColor(status)}>{status}</span>: {count}
          </span>
        ))}
        {showAllNodes && manifest?.nodes && (
          <span className="ml-2">
            • Showing all {Object.keys(manifest.nodes).filter(id => 
              manifest.nodes[id].type !== 'directory' || showAllDirectories
            ).length} project files
            {showAllDirectories && ` and directories`}
          </span>
        )}
      </div>
      
      {/* React Flow container */}
      <div 
        ref={containerRef}
        className={`p-3 rounded-md ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} overflow-hidden`} 
        style={{ height: isFullscreen ? '100vh' : maxHeight }}
      >
        <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
          {/* Loading overlay while calculating layout */}
          {isCalculatingLayout && (
            <div className="absolute inset-0 flex items-center justify-center bg-opacity-50 bg-gray-900 z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
          
          <ReactFlow
            key={flowKey} // Use the stable memoized key
            nodes={filteredNodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onPaneClick={resetHighlighting}
            onNodeMouseEnter={onNodeMouseEnter}
            onNodeMouseLeave={onNodeMouseLeave}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{
              type: 'custom',
              animated: false,
              style: {
                strokeWidth: 2,
                stroke: darkMode ? '#6B7280' : '#9CA3AF'
              }
            }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            maxZoom={2}
            attributionPosition="bottom-right"
            nodesDraggable={false} // Disable node dragging
            nodesConnectable={false} // Disable connecting nodes
            elementsSelectable={true} // Keep elements selectable
          >
            {/* Horizontal status bar for simple layouts */}
            {nodes.length > 0 && edges.length === 0 && (
              <div 
                className={`horizontal-status-bar ${darkMode ? 'dark' : 'light'}`}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  height: '4px',
                  top: '160px', // Position below the nodes
                  background: darkMode ? 
                    'linear-gradient(90deg, rgba(59,130,246,0.5) 0%, rgba(16,185,129,0.5) 50%, rgba(249,115,22,0.5) 100%)' : 
                    'linear-gradient(90deg, rgba(59,130,246,0.7) 0%, rgba(16,185,129,0.7) 50%, rgba(249,115,22,0.7) 100%)',
                  boxShadow: darkMode ? 
                    '0 0 10px rgba(59,130,246,0.3)' : 
                    '0 0 10px rgba(59,130,246,0.2)',
                  zIndex: 0
                }}
              />
            )}
            
            {/* Custom Controls instead of the default */}
            <CustomControls 
              darkMode={darkMode} 
              onZoomIn={zoomIn} 
              onZoomOut={zoomOut} 
              onFitView={() => fitView({ padding: 0.2 })}
              onRefresh={() => {
                console.log("Forcing graph refresh");
                initialLayoutApplied.current = false;
                prevNodeCount.current = 0;
                
                // Force a complete re-render
                setTimeout(() => {
                  if (isComponentMounted.current) {
                    const layoutedElements = getLayoutedElements(initialNodes, initialEdges, 'BT');
                    setNodes(layoutedElements.nodes);
                    setEdges(layoutedElements.edges);
                    
                    setTimeout(() => {
                      if (isComponentMounted.current) {
                        fitView({ padding: 0.2 });
                      }
                    }, 100);
                  }
                }, 50);
              }}
            />
            <MiniMap 
              nodeColor={node => {
                switch (node.data.status) {
                  case 'ghost': return '#9CA3AF';
                  case 'pending': return '#F59E0B';
                  case 'hydrating': return '#3B82F6';
                  case 'fresh': return '#10B981';
                  case 'stale': return '#F97316';
                  default: return '#9CA3AF';
                }
              }}
              maskColor={darkMode ? 'rgba(17, 24, 39, 0.5)' : 'rgba(243, 244, 246, 0.5)'}
              style={{ 
                height: 80, // Shrink the minimap height
                width: 120, // Shrink the minimap width
                backgroundColor: darkMode ? '#1F2937' : '#F3F4F6', // Dark mode background
                border: darkMode ? '1px solid #374151' : '1px solid #E5E7EB'
              }}
            />
            <Background color={darkMode ? '#374151' : '#E5E7EB'} gap={16} />
          </ReactFlow>
          
          {/* Add CSS for animated edges */}
          <style jsx global>{`
            .animated-edge {
              stroke-dasharray: 5;
              animation: dashdraw 1s linear infinite;
            }
            
            @keyframes dashdraw {
              from {
                stroke-dashoffset: 10;
              }
              to {
                stroke-dashoffset: 0;
              }
            }
            
            .react-flow__edge-path {
              transition: stroke 0.3s, stroke-width 0.3s, opacity 0.3s;
            }
            
            .react-flow__edge:hover .react-flow__edge-path {
              stroke-width: 3px;
              opacity: 1;
            }
            
            .custom-scrollbar::-webkit-scrollbar {
              width: 6px;
              height: 6px;
            }
            
            .custom-scrollbar::-webkit-scrollbar-track {
              background: transparent;
            }
            
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background-color: rgba(156, 163, 175, 0.5);
              border-radius: 3px;
            }
            
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background-color: rgba(156, 163, 175, 0.7);
            }
            
            /* Dark mode scrollbar */
            .dark .custom-scrollbar::-webkit-scrollbar-thumb {
              background-color: rgba(75, 85, 99, 0.5);
            }
            
            .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background-color: rgba(75, 85, 99, 0.7);
            }
          `}</style>
          
          {/* Hover card with improved visibility handling */}
          {hoverNode && hoverPosition && (
            <HydrationRowCard
              node={{
                id: hoverNode.id,
                path: hoverNode.data.path,
                status: hoverNode.data.status,
                type: hoverNode.type,
                dependencies: manifest?.nodes[hoverNode.id]?.dependencies?.map(depId => ({
                  id: depId,
                  path: manifest.nodes[depId]?.path || depId,
                  status: manifest.nodes[depId]?.status || 'ghost'
                })) || [],
                metadata: manifest?.nodes[hoverNode.id]?.metadata,
                assigned_persona_id: manifest?.nodes[hoverNode.id]?.assigned_persona_id,
                created_at: manifest?.nodes[hoverNode.id]?.created_at,
                updated_at: manifest?.nodes[hoverNode.id]?.updated_at,
                artifact_id: manifest?.nodes[hoverNode.id]?.artifact_id
              }}
              isCompleted={completedNodes.has(hoverNode.id)}
              isCurrentlyHydrating={currentlyHydrating === hoverNode.id}
              completedNodes={completedNodes}
              onFocusInGraph={focusOnNode}
              position={hoverPosition}
              visible={isHoverVisible}
              showFocusButton={false} // Don't show focus button in hover card
              contentPreview={contentPreviews[hoverNode.id]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Create a wrapper component that provides the ReactFlowProvider
export const DependencyGraph: React.FC<DependencyGraphProps> = (props) => {
  return (
    <ReactFlowProvider>
      <DependencyGraphInner {...props} />
    </ReactFlowProvider>
  );
};
